import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { supabase, TABLES } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Button from '../ui/Button'
import ThemeToggle from '../ui/ThemeToggle'
import GamificationSettings from './GamificationSettings'
import toast from 'react-hot-toast'

const { FiUsers, FiGlobe, FiMoon, FiSun, FiClock, FiSave, FiMonitor, FiLogOut, FiZap, FiSettings } = FiIcons

const FamilySettings = () => {
  const { family, setFamily, signOut } = useAuth()
  const { theme } = useTheme()
  const [loading, setLoading] = useState(false)
  const [familyName, setFamilyName] = useState(family?.name || '')
  const [activeTab, setActiveTab] = useState('general')
  const [settings, setSettings] = useState(
    family?.settings?.display || {
      theme: 'light',
      language: 'en',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      currency: 'USD',
      date_format: 'MM/DD/YYYY',
      time_format: '12h'
    }
  )

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const saveFamilyName = async () => {
    if (!familyName.trim()) {
      toast.error('Family name cannot be empty')
      return
    }

    setLoading(true)
    try {
      console.log('Updating family name for family ID:', family.id)
      const { data, error } = await supabase
        .from(TABLES.FAMILIES)
        .update({ name: familyName.trim() })
        .eq('id', family.id)
        .select()
        .single()

      if (error) {
        console.error('Family name update error:', error)
        toast.error('Failed to update family name: ' + (error.message || 'Unknown error'))
      } else {
        console.log('Family name updated successfully:', data)
        setFamily(data)
        toast.success('Family name updated! 👨‍👩‍👧‍👦')
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      toast.error('An unexpected error occurred: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const saveDisplaySettings = async () => {
    setLoading(true)
    try {
      console.log('Saving display settings for family:', family.id)
      console.log('Current family settings:', family.settings)
      console.log('New display settings:', settings)

      // Ensure we preserve existing settings and only update display
      const updatedSettings = {
        ...(family.settings || {}),
        display: settings
      }

      console.log('Updated settings object:', updatedSettings)

      const { data, error } = await supabase
        .from(TABLES.FAMILIES)
        .update({ settings: updatedSettings })
        .eq('id', family.id)
        .select()
        .single()

      if (error) {
        console.error('Settings update error:', error)
        toast.error('Failed to save display settings: ' + (error.message || 'Unknown error'))
      } else {
        console.log('Settings updated successfully:', data)
        setFamily(data)
        toast.success('Display settings saved! ⚙️')
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      toast.error('An unexpected error occurred: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Português' }
  ]

  const currencies = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' },
    { value: 'AUD', label: 'Australian Dollar (A$)' },
    { value: 'JPY', label: 'Japanese Yen (¥)' }
  ]

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Australia/Sydney'
  ]

  // Show loading state if family data isn't loaded yet
  if (!family) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading family settings...</p>
        </div>
      </Card>
    )
  }

  const tabs = [
    { id: 'general', label: 'General', icon: FiSettings },
    { id: 'gamification', label: 'Gamification', icon: FiZap }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiUsers} className="text-primary-500 text-xl" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Family Settings</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your family profile, preferences, and gamification features
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <SafeIcon icon={tab.icon} className="text-sm" />
              {tab.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <>
          {/* Family Information */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Family Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Family Name
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder="Enter your family name"
                  />
                  <Button
                    variant="primary"
                    onClick={saveFamilyName}
                    loading={loading}
                    disabled={familyName === family?.name}
                  >
                    <SafeIcon icon={FiSave} className="text-sm" />
                    Save
                  </Button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This name appears throughout the app and in shared content
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Total Children
                  </label>
                  <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {family?.children?.length || 0}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Account Created
                  </label>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {family?.created_at ? new Date(family.created_at).toLocaleDateString() : 'Unknown'}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Theme Settings */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Appearance</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Theme
                </label>
                <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center gap-3">
                    <SafeIcon 
                      icon={theme === 'dark' ? FiMoon : FiSun} 
                      className="text-primary-500 text-xl" 
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        Theme Preference
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Choose your preferred color scheme
                      </div>
                    </div>
                  </div>
                  <ThemeToggle variant="dropdown" showLabel={true} />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  The theme setting applies to your entire family account and is saved across devices.
                </p>
              </div>
            </div>
          </Card>

          {/* Localization Settings */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Localization</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={settings.language}
                  onChange={(e) => handleSettingChange('language', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  {languages.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  value={settings.timezone}
                  onChange={(e) => handleSettingChange('timezone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Currency (Future Feature)
                </label>
                <select
                  value={settings.currency}
                  onChange={(e) => handleSettingChange('currency', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 opacity-50"
                  disabled
                >
                  {currencies.map((currency) => (
                    <option key={currency.value} value={currency.value}>
                      {currency.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Coming soon: Monetary rewards and allowances
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Format
                </label>
                <select
                  value={settings.time_format}
                  onChange={(e) => handleSettingChange('time_format', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="12h">12 Hour (AM/PM)</option>
                  <option value="24h">24 Hour</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Account Actions */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Account Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={signOut}
                className="flex items-center gap-2 justify-center border-orange-200 text-orange-600 hover:bg-orange-50 dark:border-orange-800 dark:text-orange-400 dark:hover:bg-orange-900/20"
              >
                <SafeIcon icon={FiLogOut} className="text-lg" />
                Sign Out
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2 justify-center text-gray-600 dark:text-gray-400"
              >
                <SafeIcon icon={FiGlobe} className="text-lg" />
                Help Center
              </Button>
            </div>
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Note:</strong> Signing out will log you out of your account. You'll need to sign in again to access your family's data.
              </p>
            </div>
          </Card>

          {/* App Information */}
          <Card className="bg-gray-50 dark:bg-gray-800/50">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">About Chore Haven</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Version Information</h4>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  <div>App Version: 1.0.0</div>
                  <div>Last Updated: {new Date().toLocaleDateString()}</div>
                  <div>Database: Connected</div>
                  <div>Theme: {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Support & Resources</h4>
                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="w-full justify-start text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <SafeIcon icon={FiGlobe} className="text-sm" />
                    Help Center
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <SafeIcon icon={FiUsers} className="text-sm" />
                    Community Forum
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={saveDisplaySettings}
              loading={loading}
            >
              <SafeIcon icon={FiSave} className="text-sm" />
              Save Display Settings
            </Button>
          </div>
        </>
      )}

      {activeTab === 'gamification' && <GamificationSettings />}
    </div>
  )
}

export default FamilySettings